import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FieldDropdownContainerStyled,
  FieldDropdownInputStyled,
  FieldDropdownListStyled,
} from "./FieldDropdown.styled";
import {
  FieldDropdownOptionObject,
  FieldDropdownProps,
} from "./FieldDropdown.types";
import { useTranslation } from "react-i18next";

const FieldDropdown: FC<FieldDropdownProps> = ({
  value,
  name,
  label,
  options,
  placeholder,
  isRequired,
  disabled,
  errors,
  touched,
  errorText,
  className,
  addDefault = true,
  onChange,
  onClick
}) => {
  const { t } = useTranslation();
  const optionsListRef = useRef<HTMLDivElement>(null);
  const [localValue, setLocalValue] = useState<FieldDropdownOptionObject>();
  const [localOptions, setLocalOptions] =
    useState<FieldDropdownOptionObject[]>();
  const [showList, setShowList] = useState(false);

  const showError = useMemo(() => {
    return errors && errors[name] && touched && touched[name];
  }, [name, errors, touched]);

  const initOptions = useCallback((): void => {
    let _options = (options || []).map((option) =>
      typeof option === "object" ? option : { label: option, value: option },
    );
    setLocalOptions([
      ...(addDefault
        ? [{ label: placeholder || t("components.fieldDropdown.placeholder") }]
        : []),
      ..._options,
    ]);
  }, [options]);

  const handleCloseOptions = useCallback(
    (event?: Event) => {
      if (!event || !optionsListRef.current?.contains(event.target as Node)) {
        setShowList(false);
        document.removeEventListener("mousedown", handleCloseOptions, false);
      }
    },
    [localValue],
  );

  const handleClick = () => {
    if (!disabled) {
      setShowList(!showList);
      document.addEventListener("mousedown", handleCloseOptions, false);
    }
  };

  const handleOptionClick = (option: FieldDropdownOptionObject): void => {
    setLocalValue(option);
    handleCloseOptions();
    onChange && onChange(option.value);
  };

  useEffect(() => {
    if (localOptions) {
      const _option = localOptions.find((option) => option.value === value);
      setLocalValue(_option);
    }
  }, [value, localOptions]);

  useEffect(() => {
    initOptions();
  }, [options]);

  return (
    <FieldDropdownContainerStyled
      className={`${className}`}
      ref={optionsListRef}
      onClick={onClick && onClick}
    >
      <FieldDropdownInputStyled
        className={`${showList ? "field-dropdown__active" : ""}`}
        onClick={handleClick}
      >
        {label && (
          <label className="sw-field-label">
            {label} {isRequired ? "*" : ""}
          </label>
        )}
        <div
          className={`sw-field-input ${
            disabled ? "field-input__disabled" : ""
          }`}
        >
          {localValue ? (
            typeof localValue === "object" ? (
              localValue.label
            ) : (
              localValue
            )
          ) : (
            <span className="value">
              {placeholder || t("components.fieldDropdown.placeholder")}
            </span>
          )}
          <span className="icon-arrow" />
        </div>
        {showError && <div className="sw-field-error">{errorText}</div>}
      </FieldDropdownInputStyled>
      {localOptions && showList && (
        <FieldDropdownListStyled>
          <ul className="field-dropdown-items">
            {localOptions.map((option, index) => (
              <li
                className={`field-dropdown-item ${
                  option.value === localValue?.value
                    ? "field-dropdown-item__active"
                    : ""
                }`}
                key={`autocomplete-item-${index}`}
                onClick={() => handleOptionClick(option)}
              >
                {typeof option === "object" ? option.label : option}
              </li>
            ))}
          </ul>
        </FieldDropdownListStyled>
      )}
    </FieldDropdownContainerStyled>
  );
};

export default FieldDropdown;
